/**
 * Website branding accessors — `KueezSDK.getWebsiteLogo()` / `getWebsiteFavicon()`.
 *
 * Main bundle only. Attached in `src/index.ts` rather than `bootstrap.ts`, so the
 * sealed variants — which carry a baked config and never serve publisher chrome —
 * do not ship code they would never call. `scripts/check-bundles.mjs` asserts the
 * absence.
 *
 * Neither function issues a request. The logo is already in the config from
 * `init()`, and the favicon shares the site's theme asset root with it:
 *
 *   {cdn}/{assets_path}/img/head-logo.svg    ← website.logo_url
 *   {cdn}/{assets_path}/icons/favicon.ico    ← derived
 *
 * `assets_path` itself is dropped by the website normalizer (it is a build stamp
 * hosts have no other use for), so `logo_url` is the handle on that root.
 */

import { getSdkStateOrNull } from './state';

/** Where the theme hangs its images; the segment the favicon path replaces. */
const IMG_SEGMENT = '/img/';
const FAVICON_PATH = '/icons/favicon.ico';

/**
 * Public site logo URL from pool website settings.
 *
 * `''` before `init()` resolves, or when the website has no logo configured —
 * call inside `ready()` and treat `''` as "render your text fallback".
 */
export function getWebsiteLogo(): string {
  const logo = getSdkStateOrNull()?.config.website.logo_url;
  return typeof logo === 'string' ? logo.trim() : '';
}

/**
 * Site favicon URL, derived from the logo's asset root.
 *
 * `''` when there is no logo, or when its URL does not follow the theme asset
 * layout — guessing a path we cannot see would only produce a 404.
 */
export function getWebsiteFavicon(): string {
  const logo = getWebsiteLogo();
  const at = logo.lastIndexOf(IMG_SEGMENT);
  return at < 0 ? '' : logo.slice(0, at) + FAVICON_PATH;
}

/**
 * Publisher homepage — `https://{website.domain}/`.
 *
 * The config domain already accounts for `?kz_domain=`, the script attribute and
 * the hostname, so hosts must not rebuild that cascade. Falls back to the page
 * origin before `init()` resolves.
 */
export function getWebsiteHomeUrl(): string {
  const domain = getSdkStateOrNull()?.config.website.domain?.trim();
  if (domain) return `https://${domain}/`;
  return typeof location === 'undefined' ? '' : `${location.origin}/`;
}

/**
 * URL for a website setting (`privacy_url`, `tos_url`, `ccpa_url`, …).
 *
 * Falls back to `fallbackPath` resolved against the website domain, so footer
 * links work on sites that never set the setting. An absolute `fallbackPath` is
 * returned as-is.
 */
export function getSettingUrl(key: string, fallbackPath = ''): string {
  const raw = getSdkStateOrNull()?.config.settings?.[key];
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value) return value;
  if (!fallbackPath) return '';
  if (/^https?:\/\//i.test(fallbackPath)) return fallbackPath;
  const home = getWebsiteHomeUrl();
  if (!home) return '';
  return home.replace(/\/$/, '') + (fallbackPath.startsWith('/') ? fallbackPath : `/${fallbackPath}`);
}

/** The publisher's legal / about links, in the order a site footer lists them. */
export interface SiteLinks {
  privacy: string;
  about: string;
  tos: string;
  ccpa: string;
  donotsell: string;
}

/** Website setting per link, and the path it falls back to on the site domain. */
const SITE_LINKS: ReadonlyArray<readonly [keyof SiteLinks, string, string]> = [
  ['privacy', 'privacy_url', '/privacy'],
  ['about', 'about_url', 'https://weare.kueez.com/about'],
  ['tos', 'tos_url', '/tos'],
  ['ccpa', 'ccpa_url', '/ccpa'],
  ['donotsell', 'donotsell_url', '/donotsell'],
];

/**
 * Every site-footer link resolved at once — the website setting when the
 * publisher configured one, else the canonical path on the site domain.
 */
export function getSiteLinks(): SiteLinks {
  const out = {} as SiteLinks;
  for (const [key, setting, fallback] of SITE_LINKS) {
    out[key] = getSettingUrl(setting, fallback);
  }
  return out;
}

/**
 * Publisher copyright line (`footer_copyrights`), or `''` when the site has
 * none — render your own fallback.
 */
export function getFooterCopyrights(): string {
  const value = getSdkStateOrNull()?.config.settings?.footer_copyrights;
  return typeof value === 'string' ? value.trim() : '';
}
